import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import { useSesion } from '../sesion';
import { Cargando, Error } from '../componentes/comunes';

/**
 * La matriz de permisos.
 *
 * Un módulo por renglón y un rol por columna, con sus acciones dentro de
 * la celda. Al revés —un rol por renglón— la pregunta que se contesta es
 * «qué puede este rol», y la que se hace de verdad es la contraria: quién
 * puede autorizar una compra, quién puede tocar el almacén. Se lee por
 * módulo porque así es como llega la duda.
 *
 * Se guarda celda por celda, en cuanto se marca. Un botón de «guardar
 * todo» sobre una tabla de nueve por trece invita a cambiar seis cosas y
 * descubrir después cuál de ellas rompió algo.
 *
 * Dos cosas no se pueden hacer desde aquí, y la API las rechaza además de
 * que la pantalla las oculte: tocar el rol Administrador, y quitarse uno
 * mismo el permiso de editar permisos. Son las dos formas de quedarse
 * fuera de la pantalla que repara todo lo demás.
 */

type Accion = 'ver' | 'crear' | 'editar' | 'eliminar' | 'autorizar' | 'exportar';

const ACCIONES: { clave: Accion; letra: string; nombre: string }[] = [
  { clave: 'ver', letra: 'V', nombre: 'Ver' },
  { clave: 'crear', letra: 'C', nombre: 'Crear' },
  { clave: 'editar', letra: 'E', nombre: 'Editar' },
  { clave: 'eliminar', letra: 'B', nombre: 'Dar de baja' },
  { clave: 'autorizar', letra: 'A', nombre: 'Autorizar' },
  { clave: 'exportar', letra: 'X', nombre: 'Exportar' },
];

const ROL_INTOCABLE = 'admin';

interface Matriz {
  roles: { clave: string; nombre: string }[];
  modulos: {
    modulo: string;
    nombre: string;
    sinResponsable: boolean;
    porRol: { rol: string; acciones: Accion[] }[];
  }[];
  advertencias: string[];
}

export default function Roles() {
  const { usuario, puede } = useSesion();
  const [matriz, setMatriz] = useState<Matriz | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [guardando, setGuardando] = useState<string | null>(null);

  const puedeEditar = puede('usuarios', 'editar');
  const misRoles = usuario?.roles ?? [];

  const cargar = useCallback(() => {
    api.get<Matriz>('/roles/matriz').then(setMatriz).catch(setError);
  }, []);

  useEffect(cargar, [cargar]);

  async function alternar(
    modulo: string,
    rol: string,
    accion: Accion,
    actuales: Accion[],
  ) {
    const celda = `${modulo}|${rol}`;
    const acciones = actuales.includes(accion)
      ? actuales.filter((a) => a !== accion)
      : [...actuales, accion];

    setError(null);
    setGuardando(celda);
    try {
      await api.put(`/roles/${rol}/permisos/${modulo}`, { acciones });
      // Se recarga entera: cambiar un permiso puede quitarle a un módulo
      // su último responsable, y esa advertencia vive en otro renglón.
      cargar();
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(null);
    }
  }

  if (error && !matriz) return <Error error={error} />;
  if (!matriz) return <Cargando />;

  return (
    <div className="panel">
      <p className="sub">
        Qué puede hacer cada rol en cada módulo. Los cambios se guardan al
        momento y afectan a todos los usuarios que tengan ese rol.
      </p>

      <Error error={error} />

      {matriz.advertencias.length > 0 && (
        <div className="aviso error">
          <strong>Módulos sin responsable</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {matriz.advertencias.map((a) => <li key={a}>{a}</li>)}
          </ul>
        </div>
      )}

      <div className="aviso">
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {ACCIONES.map((a) => (
            <span key={a.clave} className="ayuda">
              <strong className="mono">{a.letra}</strong> {a.nombre}
            </span>
          ))}
        </div>
        {puedeEditar && (
          <div className="ayuda" style={{ marginTop: 6 }}>
            El rol Administrador no se edita desde aquí: es el que puede reparar
            todo lo demás.
          </div>
        )}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th style={{ position: 'sticky', left: 0, background: 'inherit' }}>Módulo</th>
              {matriz.roles.map((r) => (
                <th key={r.clave} style={{ whiteSpace: 'nowrap' }}>
                  {r.nombre}
                  {misRoles.includes(r.clave) && (
                    <div className="ayuda" style={{ fontWeight: 400 }}>tu rol</div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matriz.modulos.map((m) => (
              <tr key={m.modulo}>
                <td style={{ position: 'sticky', left: 0, background: 'inherit' }}>
                  {m.nombre}
                  {m.sinResponsable && (
                    <div className="ayuda" style={{ color: '#b02a37' }}>sin responsable</div>
                  )}
                </td>

                {m.porRol.map((celda) => {
                  const bloqueada = celda.rol === ROL_INTOCABLE || !puedeEditar;
                  const ocupada = guardando === `${m.modulo}|${celda.rol}`;

                  return (
                    <td key={celda.rol} style={{ opacity: ocupada ? 0.5 : 1 }}>
                      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {ACCIONES.map((a) => {
                          const tiene = celda.acciones.includes(a.clave);

                          if (bloqueada) {
                            return (
                              <span
                                key={a.clave}
                                className="mono"
                                title={`${a.nombre}: ${tiene ? 'sí' : 'no'}`}
                                style={{
                                  width: 20, textAlign: 'center',
                                  color: tiene ? '#166534' : '#c9c9c9',
                                  fontWeight: tiene ? 700 : 400,
                                }}
                              >
                                {tiene ? a.letra : '·'}
                              </span>
                            );
                          }

                          return (
                            <button
                              key={a.clave}
                              className="mono"
                              title={`${a.nombre}: ${tiene ? 'quitar' : 'dar'}`}
                              disabled={ocupada}
                              onClick={() => void alternar(
                                m.modulo, celda.rol, a.clave, celda.acciones,
                              )}
                              style={{
                                width: 24, padding: '2px 0', textAlign: 'center',
                                border: `1px solid ${tiene ? '#166534' : 'var(--border)'}`,
                                background: tiene ? '#166534' : 'transparent',
                                color: tiene ? '#fff' : '#9a9a9a',
                                fontWeight: tiene ? 700 : 400,
                                borderRadius: 4,
                                cursor: 'pointer',
                              }}
                            >
                              {a.letra}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!puedeEditar && (
        <p className="ayuda">
          Sólo puedes consultarla: editar permisos exige el permiso de editar usuarios.
        </p>
      )}
    </div>
  );
}
